import { useMemo } from 'react'
import {
    buildHierarchyTree,
    getEntityKeyFinding,
    getHierarchyEntries,
} from '../../utils/hierarchyUtils'

const UNGROUPED_ID = '__ungrouped__'
const UNGROUPED_LABEL = 'Other Runs'

/**
 * Formats a date string from epoch ms for display (e.g., "Feb 24, 2026")
 */
function formatDateLabel(epochMs) {
    return new Date(epochMs).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

/**
 * Returns a date key string (YYYY-MM-DD) for grouping runs by calendar date
 */
function getDateKey(epochMs) {
    const d = new Date(epochMs)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * The group id is the trailing `grp_*` segment of an investigation-group run
 * path (e.g. ".../investigation-groups/2026/07/15/grp_fce34284/").
 */
export function groupIdFromRunPath(runPath) {
    const match = String(runPath || '').match(/(grp_[A-Za-z0-9-]+)\/?$/)
    return match ? match[1] : null
}

/**
 * useRunTree — organizes run artifacts by the investigation GROUP that produced
 * them (the one run method, shared with the CLI). A group is whatever named
 * scope of entities the customer's config defines; its label comes from the
 * group record's own name, never from a hardcoded business noun.
 *
 * Tree structure:
 *   Group Name (nodeType: 'run_group')      e.g. "HO1", "Coastal Agents"
 *     └── Date Node (nodeType: 'date')      one cycle
 *           ├── Hierarchy Node (nodeType: 'hierarchy_node') …when configured
 *           │     └── Entity Leaf (nodeType: 'entity')
 *           └── Entity Leaf                  …when no hierarchy
 *
 * The legacy plan-based organization (Saved Plans / Ad-Hoc) was removed with
 * the plan system (2026-07-27 consolidation).
 *
 * @param {Array} runs - run objects derived from listDomainIntelligenceArtifacts
 * @param {Array} groups - group records from listGroups (groupId, groupName)
 * @returns {{ treeData: Array, getNodeById: Function }}
 */
export function useRunTree(runs = [], groups = []) {
    return useMemo(() => {
        const groupNameById = {}
        for (const group of groups) {
            if (group?.groupId) groupNameById[group.groupId] = group.groupName || group.groupId
        }

        // Bucket runs by the group that produced them.
        const runsByGroup = {}
        for (const run of runs) {
            const groupId = groupIdFromRunPath(run.runPath) || UNGROUPED_ID
            if (!runsByGroup[groupId]) runsByGroup[groupId] = []
            runsByGroup[groupId].push(run)
        }

        const nodeMap = {}
        const treeData = []

        const registerNode = (node) => {
            nodeMap[node.id] = node
            for (const child of node.children || []) {
                registerNode(child)
            }
        }

        const buildDateNodes = ({ groupKey, groupLabel, groupRuns }) => {
            const runsByDate = {}
            for (const run of groupRuns) {
                const dateKey = run.startedAt ? getDateKey(run.startedAt) : 'unknown'
                if (!runsByDate[dateKey]) runsByDate[dateKey] = []
                runsByDate[dateKey].push(run)
            }

            const sortedDates = Object.keys(runsByDate).sort((a, b) => b.localeCompare(a))

            return sortedDates.map((dateKey) => {
                const dateRuns = runsByDate[dateKey]
                const firstRun = dateRuns[0]
                const dateLabel = firstRun.startedAt
                    ? formatDateLabel(firstRun.startedAt)
                    : 'Unknown date'

                const entityNodes = []
                const seenEntities = new Set()

                for (const run of dateRuns) {
                    if (!Array.isArray(run.flaggedEntitiesSummary)) continue
                    for (const entity of run.flaggedEntitiesSummary) {
                        if (seenEntities.has(entity.entityId)) continue
                        seenEntities.add(entity.entityId)

                        const entityNodeId = `entity:${groupKey}:${dateKey}:${entity.entityId}`
                        const entityNode = {
                            id: entityNodeId,
                            label: entity.entityId,
                            nodeType: 'entity',
                            severity: entity.severity || 'unknown',
                            keyFinding: getEntityKeyFinding(entity),
                            runPath: run.runPath,
                            runId: run.runId,
                            entityId: entity.entityId,
                            displayEntityId: entity.displayEntityId || entity.displayLabel,
                            hierarchy: entity.hierarchy,
                            hitScreen: entity.hitScreen,
                        }
                        entityNodes.push(entityNode)
                        nodeMap[entityNodeId] = entityNode
                    }
                }

                const hierarchyLevels = []
                for (const entity of entityNodes) {
                    for (const entry of getHierarchyEntries(entity)) {
                        if (!hierarchyLevels.includes(entry.levelName)) {
                            hierarchyLevels.push(entry.levelName)
                        }
                    }
                }

                const hierarchyNodes =
                    hierarchyLevels.length > 0
                        ? buildHierarchyTree(entityNodes, hierarchyLevels)
                        : []

                const decorateHierarchyNode = (node) => {
                    node.dateKey = dateKey
                    node.dateLabel = dateLabel
                    node.groupLabel = groupLabel
                    // Legacy field name still read by RollupReport's header.
                    node.planName = groupLabel
                    node.runs = dateRuns
                    node.hierarchyLevels = hierarchyLevels
                    for (const child of node.children || []) {
                        if (child.nodeType === 'hierarchy_node') decorateHierarchyNode(child)
                    }
                }

                for (const hierarchyNode of hierarchyNodes) {
                    decorateHierarchyNode(hierarchyNode)
                    registerNode(hierarchyNode)
                }

                const flaggedCount = entityNodes.length
                const dateNodeId = `date:${groupKey}:${dateKey}`
                const dateNode = {
                    id: dateNodeId,
                    label: `${dateLabel} — ${flaggedCount} flagged`,
                    nodeType: 'date',
                    dateKey,
                    dateLabel,
                    flaggedCount,
                    runs: dateRuns,
                    runPath: firstRun.runPath,
                    groupLabel,
                    planName: groupLabel,
                    hierarchyLevels,
                    children: hierarchyNodes.length > 0 ? hierarchyNodes : entityNodes,
                }
                nodeMap[dateNodeId] = dateNode
                return dateNode
            })
        }

        const groupIds = Object.keys(runsByGroup)
        // Most recent activity first; the ungrouped bucket always last.
        groupIds.sort((a, b) => {
            if (a === UNGROUPED_ID) return 1
            if (b === UNGROUPED_ID) return -1
            const aLatest = Math.max(...runsByGroup[a].map((r) => r.startedAt || 0))
            const bLatest = Math.max(...runsByGroup[b].map((r) => r.startedAt || 0))
            return bLatest - aLatest
        })

        for (const groupId of groupIds) {
            const groupRuns = [...runsByGroup[groupId]].sort(
                (a, b) => (b.startedAt || 0) - (a.startedAt || 0)
            )
            const groupLabel =
                groupId === UNGROUPED_ID
                    ? UNGROUPED_LABEL
                    : groupNameById[groupId] ||
                      groupRuns[0]?.artifactLabel ||
                      groupId.replace(/^grp_/, '')

            const groupNodeId = `run_group:${groupId}`
            const groupNode = {
                id: groupNodeId,
                label: groupLabel,
                nodeType: 'run_group',
                groupId: groupId === UNGROUPED_ID ? null : groupId,
                children: buildDateNodes({ groupKey: groupId, groupLabel, groupRuns }),
            }
            treeData.push(groupNode)
            nodeMap[groupNodeId] = groupNode
        }

        const getNodeById = (id) => nodeMap[id] || null

        return { treeData, getNodeById }
    }, [runs, groups])
}
